const BACKOFF_MS = [2_000, 10_000, 45_000, 180_000]

// Backoffs that elapse near each other collapse into one announcement; the
// pending-announcement check alone would only catch same-tick timers.
export const ANNOUNCE_DELAY_MS = 50

type Failure = {
  attempts: number
  ready: boolean
  timer: ReturnType<typeof setTimeout>
}

/**
 * Guards a hydration fetch that a reactive read triggers. Success holds
 * permanently; failure holds until its backoff elapses, so only elapsed time
 * re-opens a key — never the re-render the failure itself caused.
 */
export class HydrationGuard {
  private readonly settled = new Set<string>()
  private readonly attempts = new Map<string, Promise<unknown>>()
  private readonly failures = new Map<string, Failure>()
  private announcement: ReturnType<typeof setTimeout> | null = null
  private session = 0

  constructor(private readonly onReady: () => void) {}

  ensure(key: string, run: () => Promise<unknown>): void {
    if (!this.isOpen(key)) return

    this.run(key, run).catch(() => undefined)
  }

  // Bypasses the guard to refresh a key on demand, recording the outcome so the
  // next reactive read doesn't re-fetch what this call just loaded.
  run(key: string, run: () => Promise<unknown>): Promise<unknown> {
    const session = this.session

    const attempt = run()
      .then(result => {
        if (session === this.session) {
          this.settled.add(key)
          this.clearFailure(key)
        }

        return result
      })
      .catch(error => {
        if (session === this.session) this.backOff(key)

        throw error
      })
      .finally(() => {
        // An attempt disowned by a clear must not unlock the one that replaced it.
        if (this.attempts.get(key) === attempt) this.attempts.delete(key)
      })

    this.attempts.set(key, attempt)

    return attempt
  }

  forget(key: string): void {
    this.settled.delete(key)
    this.clearFailure(key)
  }

  // Re-opens every key and disowns in-flight attempts, so a member switch can't
  // settle a key with the previous member's result.
  clear(): void {
    this.session++
    this.settled.clear()
    this.attempts.clear()
    Array.from(this.failures.keys()).forEach(key => this.clearFailure(key))
  }

  destroy(): void {
    this.clear()

    if (this.announcement) clearTimeout(this.announcement)
    this.announcement = null
  }

  private isOpen(key: string): boolean {
    if (this.settled.has(key) || this.attempts.has(key)) return false

    const failure = this.failures.get(key)

    return !failure || failure.ready
  }

  // No jitter: keys that fail together must come back together to coalesce.
  private backOff(key: string): void {
    const attempts = (this.failures.get(key)?.attempts ?? 0) + 1
    const delay = BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length) - 1]

    this.clearFailure(key)

    const failure: Failure = {
      attempts,
      ready: false,
      timer: setTimeout(() => {
        failure.ready = true
        this.announce()
      }, delay)
    }

    this.failures.set(key, failure)
  }

  private clearFailure(key: string): void {
    const failure = this.failures.get(key)

    if (!failure) return

    clearTimeout(failure.timer)
    this.failures.delete(key)
  }

  private announce(): void {
    if (this.announcement) return

    this.announcement = setTimeout(() => {
      this.announcement = null
      this.onReady()
    }, ANNOUNCE_DELAY_MS)
  }
}
